import { PublicationError } from '@streamlain/doc-publication-contract';
import { merge, Observable, of, Subject, throttleTime } from 'rxjs';

import { share } from '../../../connection';
import type { SearchIndexSource } from '../../../local-data/search-index-source';
import type {
  AggregateOptions,
  AggregateResult,
  IndexerDocument,
  IndexerSchema,
  Query,
  SearchOptions,
  SearchResult,
} from '../../../storage';
import { INDEXER_TABLES, IndexerStorageBase } from '../../../storage';
import { fromPromise } from '../../../utils/from-promise';
import { IDBConnection, type IDBConnectionOptions } from '../db';
import {
  AccountSearchIndex,
  emptyIndexWriteBatch,
  emptyIndexWriteBatches,
  hasIndexWrites,
  indexDocumentInput,
} from './account-index';
import { DataStruct } from './data-struct';
import type { IndexWriteBatch } from './encrypted-segments';
import { IDBIndexSegmentStore } from './idb-segment-store';
import { backoffRetry, exhaustMapWithTrailing } from './utils';

export class IndexedDBIndexerStorage extends IndexerStorageBase {
  static readonly identifier = 'IndexedDBIndexerStorage';
  override recommendRefreshInterval: number = 0; // force refresh on each indexer operation
  readonly connection = share(new IDBConnection(this.options));
  override isReadonly = false;
  private readonly data = new DataStruct();
  private encrypted: AccountSearchIndex | null = null;
  private readonly tableUpdate$ = new Subject<string>();

  /**
   * The write operations of IndexedDBIndexerStorage are first cached in pendingUpdates,
   * and then committed to IndexedDB in a batch through the refresh method.
   */
  private readonly pendingUpdates: Record<
    keyof IndexerSchema,
    IndexWriteBatch
  > = emptyIndexWriteBatches();

  private get account(): AccountSearchIndex | null {
    this.connection.encryption.validate();
    if (this.connection.encryption.plain) return null;
    this.encrypted ??= new AccountSearchIndex(
      new IDBIndexSegmentStore(this.connection),
      this.data,
      table => this.watchTableUpdated(table),
      table => this.emitTableUpdated(table)
    );
    return this.encrypted;
  }
  /** Sealed segments count their bytes; a plain local index cannot tell. */
  async storedBytes(): Promise<number | null> {
    return (await this.account?.segments.storedBytes()) ?? null;
  }

  override get searchSource(): SearchIndexSource {
    return this.account?.source ?? super.searchSource;
  }

  get channel() {
    return this.connection.inner.channel;
  }

  get database() {
    return this.connection.inner.db;
  }

  constructor(private readonly options: IDBConnectionOptions) {
    super();
  }

  override async search<
    T extends keyof IndexerSchema,
    const O extends SearchOptions<T>,
  >(table: T, query: Query<T>, options?: O): Promise<SearchResult<T, O>> {
    const account = this.account;
    if (account) return account.search(table, query, options);
    const trx = await this.data.readonly(this.database);
    return this.data.search(trx, table, query, options);
  }
  override async aggregate<
    T extends keyof IndexerSchema,
    const O extends AggregateOptions<T>,
  >(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O
  ): Promise<AggregateResult<T, O>> {
    const account = this.account;
    if (account) return account.aggregate(table, query, field, options);
    const trx = await this.data.readonly(this.database);
    return this.data.aggregate(trx, table, query, field as string, options);
  }
  override search$<
    T extends keyof IndexerSchema,
    const O extends SearchOptions<T>,
  >(table: T, query: Query<T>, options?: O): Observable<SearchResult<T, O>> {
    const account = this.account;
    if (account)
      return account.read$(table, () => this.search(table, query, options));
    return merge(of(1), this.watchTableUpdated(table)).pipe(
      throttleTime(3000, undefined, { leading: true, trailing: true }),
      exhaustMapWithTrailing(() => {
        return fromPromise(async () => {
          try {
            const trx = await this.data.readonly(this.database);
            return await this.data.search(trx, table, query, options);
          } catch (error) {
            console.error('search error', error);
            throw error;
          }
        }).pipe(backoffRetry());
      })
    );
  }
  override aggregate$<
    T extends keyof IndexerSchema,
    const O extends AggregateOptions<T>,
  >(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O
  ): Observable<AggregateResult<T, O>> {
    const account = this.account;
    if (account)
      return account.read$(table, () =>
        this.aggregate(table, query, field, options)
      );
    return merge(of(1), this.watchTableUpdated(table)).pipe(
      throttleTime(3000, undefined, { leading: true, trailing: true }),
      exhaustMapWithTrailing(() => {
        return fromPromise(async () => {
          try {
            const trx = await this.data.readonly(this.database);
            return await this.data.aggregate(
              trx,
              table,
              query,
              field as string,
              options
            );
          } catch (error) {
            console.error('aggregate error', error);
            throw error;
          }
        }).pipe(backoffRetry());
      })
    );
  }

  override async deleteByQuery<T extends keyof IndexerSchema>(
    table: T,
    query: Query<T>
  ): Promise<void> {
    const account = this.account;
    if (account) return account.deleteByQuery(table, query);
    this.pendingUpdates[table].deleteByQueries.push(query);
  }

  override insert<T extends keyof IndexerSchema>(
    table: T,
    document: IndexerDocument<T>
  ): Promise<void> {
    const account = this.account;
    if (account) account.insert(table, document);
    else
      this.pendingUpdates[table].inserts.push(
        indexDocumentInput(document, false)
      );
    return Promise.resolve();
  }

  override delete<T extends keyof IndexerSchema>(
    table: T,
    id: string
  ): Promise<void> {
    const account = this.account;
    if (account) account.delete(table, id);
    else this.pendingUpdates[table].deletes.push(id);
    return Promise.resolve();
  }

  override update<T extends keyof IndexerSchema>(
    table: T,
    document: IndexerDocument<T>
  ): Promise<void> {
    const account = this.account;
    if (account) account.update(table, document);
    else
      this.pendingUpdates[table].updates.push(
        indexDocumentInput(document, false)
      );
    return Promise.resolve();
  }

  override async refresh<T extends keyof IndexerSchema>(
    table: T
  ): Promise<void> {
    const account = this.account;
    if (account) return account.refresh(table);
    const trx = await this.data.readwrite(this.database);
    const tables = table ? [table] : INDEXER_TABLES;
    for (const table of tables) {
      await this.data.batchWrite(
        trx,
        table,
        this.pendingUpdates[table].deleteByQueries,
        this.pendingUpdates[table].deletes,
        this.pendingUpdates[table].inserts,
        this.pendingUpdates[table].updates
      );
      this.pendingUpdates[table] = emptyIndexWriteBatch();
    }
    this.emitTableUpdated(table);
  }

  override async refreshIfNeed(): Promise<void> {
    const account = this.account;
    if (account) return account.refreshIfNeed();
    const needRefreshTable = Object.entries(this.pendingUpdates)
      .filter(([, updates]) => hasIndexWrites(updates))
      .map(([table]) => table as keyof IndexerSchema);
    for (const table of needRefreshTable) {
      await this.refresh(table);
    }
  }

  private watchTableUpdated(table: keyof IndexerSchema) {
    return new Observable(subscriber => {
      const channel = this.channel;
      const listener = (ev: MessageEvent) => {
        if (ev.data.type === 'indexer-updated' && ev.data.table === table) {
          if (this.encrypted)
            this.encrypted.source.invalidate(
              new PublicationError('LOCAL_CONFLICT')
            );
          else subscriber.next(1);
        }
      };

      const subscription = this.tableUpdate$.subscribe(updatedTable => {
        if (updatedTable === table) {
          subscriber.next(1);
        }
      });

      channel.addEventListener('message', listener);
      return () => {
        channel.removeEventListener('message', listener);
        subscription.unsubscribe();
      };
    });
  }

  emitTableUpdated(table: keyof IndexerSchema) {
    this.tableUpdate$.next(table);
    this.channel.postMessage({ type: 'indexer-updated', table });
  }
}
